import { State } from '@app/app.reducer';
import { savedTicketHoldersFeatureKey } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.reducer';
import { isLoginSessionExpired } from '@core/utils/datetime-helpers';
import { Action, ActionReducer } from '@ngrx/store';
import { AppConstants } from '@shared/app-constants';
import {
  consoleLog,
  deleteInactiveSessions,
  getLocalStorageObject,
  getOrCreateSessionId,
  pageRunningInIframe,
  removeLocalStorageItem,
  removeLocalStorageItemsForSession
} from '@shared/app-utils';
import { UserModel } from '@store/user/user.interface';
import { isNil } from 'lodash';
import { localStorageSync } from 'ngrx-store-localstorage';

const {
  syncedGlobalReducers,
  getUserTypeFromJwt,
  syncedAdminSessionReducers,
  syncedUserSessionReducers,
  helperReducer: helperReduceKey,
  userReducer: userReducerKey,
  hideDataProtectionOnHomePageReducer: hideDataProtectionOnHomePageReducerKey,
  hideDataProtectionOnEventReducer: hideDataProtectionOnEventReducerKey
} = AppConstants;

const removeFeaturesFromLocalStorageAfterLoginSessionExpires =
  (reducer: ActionReducer<any>): ActionReducer<any> =>
  (state: State, action: Action) => {
    if (isLoginSessionExpired(state?.user?.loginTimestamp)) {
      localStorage.removeItem(savedTicketHoldersFeatureKey);
    }

    return reducer(state, action);
  };

const localStorageSyncGlobalReducer = (reducer: ActionReducer<any>): ActionReducer<any> =>
  localStorageSync({
    keys: !pageRunningInIframe() ? syncedGlobalReducers : [],
    rehydrate: true
  })(reducer);

const localStorageSyncSessionReducer = (reducer: ActionReducer<any>): ActionReducer<any> => {
  const sessionId = getOrCreateSessionId();
  const helperReducer = getLocalStorageObject(helperReduceKey);

  if (helperReducer?.reloadRequired) {
    consoleLog('Page reset and reload was requested, clearing localStorage for the current session...');

    removeLocalStorageItemsForSession(sessionId);

    removeLocalStorageItem(userReducerKey);
    removeLocalStorageItem(hideDataProtectionOnHomePageReducerKey);
    removeLocalStorageItem(hideDataProtectionOnEventReducerKey);
  }

  deleteInactiveSessions();

  const synchronizedSessionReducers = () => {
    const loggedInUser: UserModel = getLocalStorageObject(userReducerKey)?.user;
    const isIE = !!navigator.userAgent.match(/Trident/);
    const isAdmin = !isNil(loggedInUser) && getUserTypeFromJwt(loggedInUser) === 'admin';

    if (!pageRunningInIframe()) {
      return isAdmin || isIE ? syncedAdminSessionReducers : syncedUserSessionReducers;
    }

    return [];
  };

  return localStorageSync({
    keys: synchronizedSessionReducers(),
    rehydrate: true,
    storageKeySerializer: (key) => `${sessionId}_${key}`
  })(reducer);
};

export default [
  removeFeaturesFromLocalStorageAfterLoginSessionExpires,
  localStorageSyncGlobalReducer,
  localStorageSyncSessionReducer
];
